// Page-view trigger for announcements, surveys and tours (#25610). Pure: the API validates with
// these before it writes, the admin field tests a path with them, and the app decides with them
// when a page-view item shows.
//
//   validatePathPattern(pattern)           -> string | null   one problem, or null when valid
//   matchesPathPattern(pattern, pathname)  -> boolean         does this page fire the trigger?
//   isPageViewTrigger(item)                -> boolean         is the item held until a page matches?
//   shouldShowOnPage(item, pathname)       -> boolean         the delivery rule for one item
//   pickPageViewItem(items, pathname, ok)  -> item | null     the newest page-view item to show here
//   hasTriggerFired(item)                  -> boolean         may it show in history and badges yet?

/**
 * `startup`: the app shows the item when it loads (the default; a stored item with no trigger).
 * `page-view`: the app holds the item until the user opens a page that matches `pathPattern`.
 * Surveys keep their own names: `on-login` is their `startup`, and they also have `manual`.
 */
export const ENGAGEMENT_TRIGGERS = ['startup', 'page-view'] as const
export type EngagementTrigger = (typeof ENGAGEMENT_TRIGGERS)[number]

export const PAGE_VIEW_TRIGGER = 'page-view' as const

export const MAX_PATH_PATTERN_LENGTH = 200

/** The fields the page-view rule reads. Announcements, surveys and tours all carry them. */
export interface PageTriggerFields {
  trigger?: string | null
  pathPattern?: string | null
}

/** Drops the query string and hash, and a trailing slash (but keeps the root `/`). */
export function normalizePath(path: string): string {
  let out = (path ?? '').trim()
  const cut = out.search(/[?#]/)
  if (cut >= 0) out = out.slice(0, cut)
  while (out.length > 1 && out.endsWith('/')) out = out.slice(0, -1)
  return out
}

/**
 * The problem with a path pattern, or `null` when it is valid. A pattern is a path that starts
 * with `/`, where `*` matches any run of characters (including `/`). No query string, no hash,
 * no spaces.
 */
export function validatePathPattern(pattern: string | null | undefined): string | null {
  const value = (pattern ?? '').trim()
  if (!value) return 'a page-view trigger needs a page path'
  if (value.length > MAX_PATH_PATTERN_LENGTH)
    return `the page path is longer than ${MAX_PATH_PATTERN_LENGTH} characters`
  if (!value.startsWith('/')) return 'the page path must start with /'
  if (/[?#]/.test(value)) return 'the page path cannot have a query string (?) or hash (#)'
  if (/\s/.test(value)) return 'the page path cannot contain spaces'
  if (/^https?:/i.test(value) || value.startsWith('//')) return 'use the path only, not the full URL'
  return null
}

/**
 * Glob match where `*` matches any run of characters (including none). No regex, so a pattern an
 * admin typed cannot backtrack badly: one pass with a single fallback point, O(pattern × path).
 */
function globMatch(pattern: string, text: string): boolean {
  let p = 0
  let t = 0
  let star = -1
  let resume = 0
  while (t < text.length) {
    if (p < pattern.length && pattern[p] !== '*' && pattern[p] === text[t]) {
      p++
      t++
    } else if (p < pattern.length && pattern[p] === '*') {
      star = p++
      resume = t
    } else if (star !== -1) {
      p = star + 1
      t = ++resume
    } else {
      return false
    }
  }
  while (p < pattern.length && pattern[p] === '*') p++
  return p === pattern.length
}

/**
 * True when `pathname` (usually `location.pathname`) matches `pattern`. Case-insensitive. A
 * trailing slash, query string and hash are ignored on both sides. An invalid pattern matches
 * nothing.
 */
export function matchesPathPattern(pattern: string | null | undefined, pathname: string | null | undefined): boolean {
  if (validatePathPattern(pattern) !== null || typeof pathname !== 'string') return false
  return globMatch(normalizePath(pattern as string).toLowerCase(), normalizePath(pathname).toLowerCase())
}

export function isPageViewTrigger(item: PageTriggerFields | null | undefined): boolean {
  return item?.trigger === PAGE_VIEW_TRIGGER
}

/**
 * The delivery rule: a page-view item shows only on a page that matches its pattern. Any other
 * trigger is not a page-view item, so this returns false for it; startup delivery decides those.
 */
export function shouldShowOnPage(
  item: PageTriggerFields | null | undefined,
  pathname: string | null | undefined
): boolean {
  return isPageViewTrigger(item) && matchesPathPattern(item?.pathPattern, pathname)
}

/**
 * The page-view item to show on `pathname` now, or null: the newest (by `publishedAt`) whose
 * pattern matches and that `isOpen` accepts. `isOpen` is the caller's "once" rule, from state it
 * already stores (unread, unanswered, not dismissed) plus anything shown in this tab. Startup
 * items are never returned.
 */
export function pickPageViewItem<T extends PageTriggerFields & { id: string; publishedAt?: string | null }>(
  items: readonly T[],
  pathname: string | null | undefined,
  isOpen: (item: T) => boolean
): T | null {
  return (
    [...items]
      .sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''))
      .find((item) => shouldShowOnPage(item, pathname) && isOpen(item)) ?? null
  )
}

/** The per-user state timestamps `hasTriggerFired` reads. Each surface has some of them. */
export interface TriggerStateFields extends PageTriggerFields {
  viewedAt?: string | null
  dismissedAt?: string | null
  ctaClickedAt?: string | null
  answeredAt?: string | null
}

/**
 * Whether the item has reached the user yet, so it may show in a history list or an unread badge.
 * A startup item has, as soon as it is delivered. A page-view item has only once it showed: the
 * user has a view, dismiss, CTA click or answer for it. Until then a page-view item stays hidden,
 * so a "welcome to this page" item cannot be read before the user opens the page.
 */
export function hasTriggerFired(item: TriggerStateFields | null | undefined): boolean {
  if (!item) return false
  if (!isPageViewTrigger(item)) return true
  return !!(item.viewedAt || item.dismissedAt || item.ctaClickedAt || item.answeredAt)
}

/**
 * The problem with an item's trigger fields, or `null`. Only a page-view trigger needs (and
 * checks) a pattern; other triggers ignore `pathPattern`.
 */
export function validatePageTrigger(item: PageTriggerFields): string | null {
  return isPageViewTrigger(item) ? validatePathPattern(item.pathPattern) : null
}

/** The `trigger` property for `announcement_viewed`, `survey_started` and `tour_started`. */
export function triggerProperty(item: PageTriggerFields | null | undefined): string {
  return item?.trigger || 'startup'
}
